/**
 * Scroll animation engine — By Shahad Beauty
 * See docs/Animation-System.md for the full contract.
 *
 * Responsibilities:
 *   - flip `data-fx-in` on one-shot / replay elements as they enter the viewport
 *   - render above-the-fold elements in their final state instantly, to keep the hero
 *     out of the LCP measurement
 *   - apply stagger delays inside `[data-fx-stagger]` containers
 *   - load the rAF fallback, once, only if the browser lacks scroll-driven animations
 *   - rescan when the theme editor re-renders a section
 *
 * This module is deliberately effect-agnostic. It never knows what "fade" or "clip"
 * look like — it only toggles attributes that assets/animations.css reacts to. Adding a
 * new one-shot effect therefore needs no changes here.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Elements already handed to the observer, so a rescan never double-observes. */
const tracked = new WeakSet();

/** The rAF fallback is imported at most once per page. */
let fallbackRequested = false;

let observer = null;
let hasScannedOnce = false;

/**
 * Scrubbed elements are driven by the view() timeline in CSS, or by the fallback
 * module. Either way the observer must leave them alone.
 */
const isScrubbed = (element) => element.dataset.fxPlay === 'scrub';

/** Collects `[data-fx]` within a root, including the root itself. */
function collect(root) {
  const found = Array.from(root.querySelectorAll('[data-fx]'));
  if (root instanceof Element && root.hasAttribute('data-fx')) found.unshift(root);
  return found;
}

/**
 * Direct animating descendants of a container — the ones a stagger should sequence.
 * "Direct" means no other `[data-fx]` element sits between them and the container, so
 * arbitrary wrapper markup in between is fine but a nested animated group is not
 * flattened into its parent's sequence.
 */
function staggerTargets(container) {
  return Array.from(container.querySelectorAll('[data-fx]')).filter((element) => {
    const nearest = element.parentElement?.closest('[data-fx]');
    return !nearest || nearest === container || !container.contains(nearest);
  });
}

function staggerStep() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--fx-stagger');
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 80;
}

/**
 * Adds the sequence delay on top of whatever delay the merchant set on the block, so a
 * hand-tuned offset and a stagger compose rather than fight.
 */
function applyStagger(container) {
  const step = staggerStep();
  if (step <= 0) return;

  staggerTargets(container).forEach((element, index) => {
    const own = parseInt(element.dataset.fxDelay ?? '0', 10) || 0;
    element.style.setProperty('--fx-delay', `${own + index * step}ms`);
  });
}

function onIntersect(entries) {
  for (const entry of entries) {
    const element = entry.target;

    if (entry.isIntersecting) {
      element.setAttribute('data-fx-in', '');
      if (element.dataset.fxPlay !== 'replay') {
        observer.unobserve(element);
        tracked.delete(element);
      }
    } else if (element.dataset.fxPlay === 'replay') {
      element.removeAttribute('data-fx-in');
    }
  }
}

function getObserver() {
  observer ??= new IntersectionObserver(onIntersect, {
    // Wait until the element is a little way into the viewport before revealing, so the
    // motion reads as deliberate rather than clipping the bottom edge.
    rootMargin: '0px 0px -10% 0px',
    threshold: 0,
  });
  return observer;
}

/**
 * @param {ParentNode} root
 * @param {boolean} allowInstant - skip animation for anything already on screen. True on
 *   the initial page scan only; sections re-rendered by the theme editor should animate
 *   so the merchant can actually preview what they just configured.
 */
function scan(root, allowInstant) {
  const elements = collect(root);
  if (elements.length === 0) return;

  // Read every rect before writing any attribute, so this costs one layout pass
  // rather than one per element.
  const measured = elements.map((element) => ({
    element,
    aboveFold: allowInstant && element.getBoundingClientRect().top < window.innerHeight,
  }));

  for (const { element, aboveFold } of measured) {
    if (isScrubbed(element)) {
      requestFallback();
      continue;
    }

    if (tracked.has(element) || element.hasAttribute('data-fx-in')) continue;

    if (aboveFold) {
      element.setAttribute('data-fx-instant', '');
      element.setAttribute('data-fx-in', '');
      continue;
    }

    tracked.add(element);
    getObserver().observe(element);
  }

  const containers =
    root instanceof Element && root.hasAttribute('data-fx-stagger')
      ? [root, ...root.querySelectorAll('[data-fx-stagger]')]
      : Array.from(root.querySelectorAll('[data-fx-stagger]'));

  containers.forEach(applyStagger);
}

/**
 * Browsers with `animation-timeline: view()` need nothing: the scrub runs off the main
 * thread from CSS alone. Everything else pulls in the rAF module, once.
 */
function requestFallback() {
  if (fallbackRequested) return;
  if (CSS.supports('animation-timeline', 'view()')) {
    fallbackRequested = true;
    return;
  }

  fallbackRequested = true;
  import('@theme/scroll-effects-fallback')
    .then((module) => module.start())
    .catch(() => {
      // A missing fallback must never leave content hidden. Scrubbed elements are the
      // only ones this module has not already resolved, so free them.
      document
        .querySelectorAll('[data-fx][data-fx-play="scrub"]')
        .forEach((element) => element.setAttribute('data-fx-instant', ''));
    });
}

/**
 * Arms the engine.
 *
 * `.fx-js` is added here rather than by an inline script in the head, and that ordering
 * is the whole trick: until this runs, no hidden-state rule matches and the page renders
 * plainly. So a module that never loads, 404s or throws cannot blank the storefront, and
 * the hero paints at full opacity immediately instead of fading in late and dragging LCP
 * with it.
 *
 * Adding the class, measuring, and marking the above-the-fold elements all happen in one
 * synchronous block. The browser cannot paint between those steps, so nothing above the
 * fold ever flashes hidden.
 */
function init() {
  // The CSS handles reduced motion with !important, including for sections added later,
  // so there is nothing to observe and nothing to scrub.
  if (REDUCED_MOTION.matches) return;

  const root = document.documentElement;
  root.classList.add('fx-js');

  try {
    scan(document, !hasScannedOnce);
    hasScannedOnce = true;
  } catch (error) {
    // Never leave content stranded at opacity 0 because of a bug in here.
    root.classList.remove('fx-js');
    throw error;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// A visitor can flip the OS setting mid-session.
REDUCED_MOTION.addEventListener('change', () => {
  if (REDUCED_MOTION.matches) {
    observer?.disconnect();
    observer = null;
  } else {
    init();
  }
});

// Theme editor: sections are replaced over AJAX, so their elements are brand new.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scan(event.target, false);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element) || !observer) return;
  collect(event.target).forEach((element) => {
    observer.unobserve(element);
    tracked.delete(element);
  });
});

export { scan };
